import apiV2 from "api-v2";

import type {
	CoiResolutionPayload,
	EventGrantInvitePayload,
	EventGrantResource,
} from "../types/eventGrant.type";

/**
 * RBAC event-grant + COI + resource-capability API client (PRO-413).
 *
 * Thin wrappers over `apiV2` (§ coding standards: services are thin axios
 * wrappers). Endpoints/verbs mirror the merged backend surface:
 *   - Task 007  POST /rfx/{rfx}/panel/invite       (EventGrantController@invitePanel)
 *   - Task 007  POST /rfx/{rfx}/approvers/invite    (EventGrantController@inviteApprover)
 *   - Task 006  PATCH /rfx/{rfx}/panel/{member}/coi (PanelCoiController@update)
 *
 * The global resource-capability read (`GET /rbac/me/permissions/resource`,
 * Task 011) lives in the global `useResourceCapability` hook, not here — it is
 * not RFx-scoped.
 *
 * All calls THROW on non-2xx so the react-query layer surfaces the
 * server-authoritative verdict (COI 409, capability 403) to the caller —
 * these functions never swallow the error into a null.
 */

/** Invite a panel-side principal (panel_member | moderator | observer). */
export const invitePanelMember = async (
	rfxId: number,
	payload: EventGrantInvitePayload
): Promise<EventGrantResource> => {
	const response = await apiV2.post(`/rfx/${rfxId}/panel/invite`, payload);
	return response.data.data as EventGrantResource;
};

/** Invite an approver-side principal (rfx_approver | bid_approver | bid_observer). */
export const inviteApprover = async (
	rfxId: number,
	payload: EventGrantInvitePayload
): Promise<EventGrantResource> => {
	const response = await apiV2.post(`/rfx/${rfxId}/approvers/invite`, payload);
	return response.data.data as EventGrantResource;
};

/** Resolve a COI (Rule 3): `remove` or `override` (override needs a reason). */
export const resolvePanelCoi = async (
	rfxId: number,
	memberId: number,
	payload: CoiResolutionPayload
): Promise<void> => {
	await apiV2.patch(`/rfx/${rfxId}/panel/${memberId}/coi`, payload);
};
